/* ══ F15 STEP 4.1 — HIS RULE TABS, THE SERVER'S TWIN OF `CoachTabs.swift` ════════════════════════════════════════════════
 * A tab is a TARGET (the row's numbers, n weeks, or "every clean session/week") → an ACTION (change the row's fields, change
 * the exercise, deactivate / reactivate, or — a standing tab only — run a §5 rule unchanged). Tabs are ORDERED STEPS:
 *     {"rule": "tabs", "steps": [{"target": {…}, "action": {…}}, …]}
 * The live tab is DERIVED (`liveStep`): replayed over the row's weeks, a tab FIRED when its target was met AND the next week
 * shows its action; on the current week a met target is DUE. `migrate` turns every old rule kind into tabs (a ladder into
 * `next` steps, a §5 kind into one standing tab running it unchanged). Design: `docs/F15-COACH-ENGINE-DESIGN.md` §9.
 *
 * F15 4.1a′ — THE PER-SET TABLE (additive): a target may name each set (`{table: [{reps?,kg?,seconds?}], sessions?}`) and a
 * `set` action may write the WHOLE set table (`{table: [{type?,reps?,kg?,seconds?,rest?}], tempo?, note?, each_side?}`) —
 * the card's own rows, so the card itself can edit a step. The uniform keys stay readable; the migration is unchanged.
 *
 * 🔴 THE SWIFT BINDS, SENTENCE FOR SENTENCE. Pinned by `workoutRuleCorpus.json` → `tabs` (shapes, migration, live step,
 * words; `ruleTabs.test.ts`) and by `progression-fixture.json` (the `f15/tabs-*` cases and every case MIGRATED). JSON reads
 * as the Swift's `JSONValue` reads (`coachLadder.ts`'s header): a JSON `null` value is an absent key.
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

import { LADDER_RULE, type LadderLogged, kgText, reached, targetText } from "./coachLadder.ts";
import type { SetRow } from "./progression.ts";
import { isRampSet } from "./progression.ts";

export const TABS_RULE = "tabs";

export type Every = "session" | "week";
/** `CoachTabs.TargetSet` — one set of a per-set target; absent = not asked of that set. */
export interface TabTargetSet { reps: number | null; kg: number | null; seconds: number | null }
export type TabTarget =
  | { kind: "numbers"; reps: number | null; kg: number | null; seconds: number | null; sessions: 1 | 2 }
  | { kind: "perSet"; table: TabTargetSet[]; sessions: 1 | 2 }
  | { kind: "weeks"; weeks: number }
  | { kind: "every"; every: Every };

/** `coach_workout_sets.set_type`. */
export type SetType = "regular" | "warm_up" | "drop_set" | "failure";
export const SET_TYPES: readonly SetType[] = ["regular", "warm_up", "drop_set", "failure"];
/** `CoachTabs.TableSet` — one row of an action's set table, the card's own row: absent = a blank cell. */
export interface TabSet { type: SetType; reps: number | null; kg: number | null; seconds: number | null; rest: number | null }

/** `CoachTabs.Change` — absent = null. `table` replaces the whole set table (ramp rows included) and excludes the uniform
 *  set fields; `eachSide` is the row's "Each side". */
export interface TabChange {
  sets: number | null; reps: number | null; kg: number | null; seconds: number | null; rest: number | null;
  tempo: string | null; note: string | null; table: TabSet[] | null; eachSide: boolean | null;
}
export type TabAction =
  | { kind: "set"; change: TabChange }
  | { kind: "next"; next: string }
  | { kind: "deactivate" }
  | { kind: "activate" }
  | { kind: "rule"; rule: Record<string, unknown> };

export interface TabStep { target: TabTarget; note: string | null; action: TabAction }
export interface TabSteps { steps: TabStep[]; brokenAt: number | null }

const isObj = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v);
const intValue = (v: unknown): number | null => (typeof v === "number" && Number.isInteger(v) ? v : null);
const doubleValue = (v: unknown): number | null => (typeof v === "number" ? v : null);
const text = (v: unknown): string | null => (typeof v === "string" ? v : null);
const present = (o: Record<string, unknown>, k: string): boolean => o[k] !== undefined && o[k] !== null;
const keysOf = (o: Record<string, unknown>): string[] => Object.keys(o).filter((k) => present(o, k));

/** `CoachTabs.Change.touchesRow` — the row-level fields (tempo · notes · each side), written by a coach week since 4.1b. */
export const touchesRow = (c: TabChange): boolean => c.tempo !== null || c.note !== null || c.eachSide !== null;
/** `CoachTabs.Change.touchesSets` — the fields a set row carries. */
export const touchesSets = (c: TabChange): boolean =>
  c.sets !== null || c.reps !== null || c.kg !== null || c.seconds !== null || c.rest !== null || c.table !== null;

/** `CoachTabs.RowEdit` (F15 4.1b) — what a step writes on the ROW itself; null = left as it is. */
export interface RowEdit { tempo: string | null; note: string | null; eachSide: boolean | null; hidden: boolean | null }
/** `CoachTabs.RowFacts` — the row as it is written. */
export interface RowFacts { tempo: string | null; note: string | null; eachSide: boolean; hidden: boolean }
export const NO_ROW_FACTS: RowFacts = { tempo: null, note: null, eachSide: false, hidden: false };

/** `CoachTabs.rowEdit` — the row edit an action asks for, minus every field the row already holds. An empty edit is null. */
export function rowEdit(action: TabAction, row: RowFacts): RowEdit | null {
  const e: RowEdit = { tempo: null, note: null, eachSide: null, hidden: null };
  if (action.kind === "set") {
    const c = action.change;
    if (c.tempo !== null && c.tempo !== row.tempo) e.tempo = c.tempo;
    if (c.note !== null && c.note !== row.note) e.note = c.note;
    if (c.eachSide !== null && c.eachSide !== row.eachSide) e.eachSide = c.eachSide;
  } else if (action.kind === "deactivate") { if (!row.hidden) e.hidden = true; }
  else if (action.kind === "activate") { if (row.hidden) e.hidden = false; }
  return e.tempo === null && e.note === null && e.eachSide === null && e.hidden === null ? null : e;
}

/** `CoachTabs.Target.judgedByLog` — a target the row's LOG meets (the numbers, uniform or per set). */
export const judgedByLog = (t: TabTarget): t is Extract<TabTarget, { kind: "numbers" | "perSet" }> =>
  t.kind === "numbers" || t.kind === "perSet";

/** `CoachTabs.isTabs`. */
export function isTabs(config: unknown): boolean { return isObj(config) && config.rule === TABS_RULE; }

/** `CoachTabs.steps` — null when the config is not tabs; the first unreadable step ENDS the list there. */
export function tabSteps(config: unknown): TabSteps | null {
  if (!isTabs(config)) return null;
  const raw = (config as Record<string, unknown>).steps;
  if (!Array.isArray(raw)) return { steps: [], brokenAt: 0 };
  const steps: TabStep[] = [];
  for (let i = 0; i < raw.length; i++) {
    const step = readStep(raw[i]);
    if (step === null) return { steps, brokenAt: i };
    steps.push(step);
  }
  return { steps, brokenAt: null };
}

/** `CoachTabs.step`. */
export function readStep(item: unknown): TabStep | null {
  if (!isObj(item) || !isObj(item.target) || !isObj(item.action)) return null;
  const t = item.target, a = item.action;
  let note: string | null = null;
  if (present(t, "note")) { const n = text(t.note); if (n === null) return null; note = n; }
  const tk = keysOf(t).filter((k) => k !== "note");
  let target: TabTarget;
  if (tk.includes("every")) {
    const e = text(t.every);
    if (tk.length !== 1 || (e !== "session" && e !== "week")) return null;
    target = { kind: "every", every: e };
  } else if (tk.includes("weeks")) {
    const n = intValue(t.weeks);
    if (tk.length !== 1 || n === null || n < 1 || n > 52) return null;
    target = { kind: "weeks", weeks: n };
  } else {
    let sessions: 1 | 2 = 1;
    if (present(t, "sessions")) {
      const s = intValue(t.sessions);
      if (s !== 1 && s !== 2) return null;
      sessions = s;
    }
    if (tk.includes("table")) {
      if (!tk.every((k) => k === "table" || k === "sessions")) return null;
      const table = readTargetTable(t.table);
      if (table === null) return null;
      target = { kind: "perSet", table, sessions };
    } else {
      if (!tk.every((k) => k === "reps" || k === "kg" || k === "seconds" || k === "sessions")) return null;
      const reps = intValue(t.reps), kg = doubleValue(t.kg), seconds = intValue(t.seconds);
      if (present(t, "reps") && (reps ?? 0) < 1) return null;
      if (present(t, "kg") && (kg ?? 0) <= 0) return null;
      if (present(t, "seconds") && (seconds ?? 0) < 1) return null;
      if (reps === null && kg === null && seconds === null) return null;
      target = { kind: "numbers", reps, kg, seconds, sessions };
    }
  }
  const ak = keysOf(a);
  if (ak.length !== 1) return null;
  let action: TabAction;
  switch (ak[0]) {
    case "set": {
      const c = isObj(a.set) ? readChange(a.set) : null;
      if (c === null) return null;
      action = { kind: "set", change: c };
      break;
    }
    case "next": {
      const next = text(a.next);
      if (next === null || next === "") return null;
      action = { kind: "next", next: next.toLowerCase() };
      break;
    }
    case "deactivate": if (a.deactivate !== true) return null; action = { kind: "deactivate" }; break;
    case "activate": if (a.activate !== true) return null; action = { kind: "activate" }; break;
    case "rule": {
      const r = a.rule;
      const kind = isObj(r) ? text(r.rule) : null;
      if (!isObj(r) || kind === null || kind === "" || kind === TABS_RULE || kind === LADDER_RULE) return null;
      action = { kind: "rule", rule: r };
      break;
    }
    default: return null;
  }
  if ((target.kind === "every") !== (action.kind === "rule")) return null;
  return { target, note, action };
}

/** A per-set target: 1–20 sets, each naming at least one of reps / kg / seconds and nothing else (the numbers' own bounds). */
function readTargetTable(v: unknown): TabTargetSet[] | null {
  if (!Array.isArray(v) || v.length < 1 || v.length > 20) return null;
  const out: TabTargetSet[] = [];
  for (const e of v) {
    if (!isObj(e) || !keysOf(e).every((k) => k === "reps" || k === "kg" || k === "seconds")) return null;
    const reps = intValue(e.reps), kg = doubleValue(e.kg), seconds = intValue(e.seconds);
    if (present(e, "reps") && (reps ?? 0) < 1) return null;
    if (present(e, "kg") && (kg ?? 0) <= 0) return null;
    if (present(e, "seconds") && (seconds ?? 0) < 1) return null;
    if (reps === null && kg === null && seconds === null) return null;
    out.push({ reps, kg, seconds });
  }
  return out;
}

/** An action's set table: 1–20 rows, at least one a working set (not a warm-up); `type` absent = regular. */
function readActionTable(v: unknown): TabSet[] | null {
  if (!Array.isArray(v) || v.length < 1 || v.length > 20) return null;
  const out: TabSet[] = [];
  for (const e of v) {
    if (!isObj(e) || !keysOf(e).every((k) => ["type", "reps", "kg", "seconds", "rest"].includes(k))) return null;
    const row: TabSet = { type: "regular", reps: null, kg: null, seconds: null, rest: null };
    if (present(e, "type")) {
      const ty = text(e.type);
      if (ty === null || !(SET_TYPES as readonly string[]).includes(ty)) return null;
      row.type = ty as SetType;
    }
    if (present(e, "reps")) { const x = intValue(e.reps); if (x === null || x < 1) return null; row.reps = x; }
    if (present(e, "kg")) { const x = doubleValue(e.kg); if (x === null || x < 0) return null; row.kg = x; }
    if (present(e, "seconds")) { const x = intValue(e.seconds); if (x === null || x < 1) return null; row.seconds = x; }
    if (present(e, "rest")) { const x = intValue(e.rest); if (x === null || x < 0) return null; row.rest = x; }
    out.push(row);
  }
  return out.some((r) => r.type !== "warm_up") ? out : null;
}

function readChange(s: Record<string, unknown>): TabChange | null {
  const k = keysOf(s);
  const allowed = ["sets", "reps", "kg", "seconds", "rest", "tempo", "note", "table", "each_side"];
  if (k.length === 0 || !k.every((x) => allowed.includes(x))) return null;
  // The table IS the sets: a uniform set field beside it would be a second statement of one row.
  if (k.includes("table") && k.some((x) => ["sets", "reps", "kg", "seconds", "rest"].includes(x))) return null;
  const c: TabChange = { sets: null, reps: null, kg: null, seconds: null, rest: null, tempo: null, note: null, table: null,
                         eachSide: null };
  if (present(s, "sets")) { const v = intValue(s.sets); if (v === null || v < 1 || v > 20) return null; c.sets = v; }
  if (present(s, "reps")) { const v = intValue(s.reps); if (v === null || v < 1) return null; c.reps = v; }
  if (present(s, "kg")) { const v = doubleValue(s.kg); if (v === null || v < 0) return null; c.kg = v; }
  if (present(s, "seconds")) { const v = intValue(s.seconds); if (v === null || v < 1) return null; c.seconds = v; }
  if (present(s, "rest")) { const v = intValue(s.rest); if (v === null || v < 0) return null; c.rest = v; }
  if (present(s, "tempo")) { const v = text(s.tempo); if (v === null || v === "") return null; c.tempo = v; }
  if (present(s, "note")) { const v = text(s.note); if (v === null) return null; c.note = v; }
  if (present(s, "table")) { const v = readActionTable(s.table); if (v === null) return null; c.table = v; }
  if (present(s, "each_side")) { if (typeof s.each_side !== "boolean") return null; c.eachSide = s.each_side; }
  return c;
}

/** `CoachTabs.json(_ steps:)` — absent fields stay absent. */
export function tabsJson(steps: readonly TabStep[]): { rule: string; steps: Record<string, unknown>[] } {
  return { rule: TABS_RULE, steps: steps.map(stepJson) };
}

export function stepJson(step: TabStep): Record<string, unknown> {
  const t: Record<string, unknown> = {};
  const tg = step.target;
  if (tg.kind === "numbers") {
    if (tg.reps !== null) t.reps = tg.reps;
    if (tg.kg !== null) t.kg = tg.kg;
    if (tg.seconds !== null) t.seconds = tg.seconds;
    t.sessions = tg.sessions;
  } else if (tg.kind === "perSet") {
    t.table = tg.table.map((e) => {
      const o: Record<string, unknown> = {};
      if (e.reps !== null) o.reps = e.reps;
      if (e.kg !== null) o.kg = e.kg;
      if (e.seconds !== null) o.seconds = e.seconds;
      return o;
    });
    t.sessions = tg.sessions;
  } else if (tg.kind === "weeks") t.weeks = tg.weeks;
  else t.every = tg.every;
  if (step.note !== null) t.note = step.note;
  let a: Record<string, unknown>;
  const ac = step.action;
  if (ac.kind === "set") {
    const s: Record<string, unknown> = {};
    for (const k of ["sets", "reps", "kg", "seconds", "rest", "tempo", "note"] as const) {
      if (ac.change[k] !== null) s[k] = ac.change[k];
    }
    if (ac.change.table !== null) s.table = ac.change.table.map(setJson);
    if (ac.change.eachSide !== null) s.each_side = ac.change.eachSide;
    a = { set: s };
  } else if (ac.kind === "next") a = { next: ac.next };
  else if (ac.kind === "deactivate") a = { deactivate: true };
  else if (ac.kind === "activate") a = { activate: true };
  else a = { rule: ac.rule };
  return { target: t, action: a };
}

/** One row of an action's table, canonical: `type` written only when not regular, absent fields absent. */
function setJson(r: TabSet): Record<string, unknown> {
  const o: Record<string, unknown> = {};
  if (r.type !== "regular") o.type = r.type;
  if (r.reps !== null) o.reps = r.reps;
  if (r.kg !== null) o.kg = r.kg;
  if (r.seconds !== null) o.seconds = r.seconds;
  if (r.rest !== null) o.rest = r.rest;
  return o;
}

/** `CoachTabs.nextIds` — every exercise a step names as its `next`. */
export function tabNextIds(config: unknown): string[] {
  return (tabSteps(config)?.steps ?? []).flatMap((s) => (s.action.kind === "next" ? [s.action.next] : []));
}

/** `CoachTabs.rest(of:after:)` — the steps AFTER the first `next` step naming `exerciseId`; null when none names it. */
export function tabsRest(config: unknown, exerciseId: string): TabStep[] | null {
  const parsed = tabSteps(config);
  if (parsed === null) return null;
  const id = exerciseId.toLowerCase();
  const at = parsed.steps.findIndex((s) => s.action.kind === "next" && s.action.next === id);
  return at < 0 ? null : parsed.steps.slice(at + 1);
}

/** `CoachTabs.migrate` — the old rule kinds as tabs (the header). */
export function migrate(config: unknown): unknown {
  if (config === undefined || config === null) return null;
  if (!isObj(config)) return config;
  const kind = text(config.rule);
  if (kind === null || kind === "") return config;
  if (kind === TABS_RULE) return config;
  if (kind === LADDER_RULE) {
    const links = Array.isArray(config.links) ? config.links : [];
    return {
      rule: TABS_RULE,
      steps: links.map((link) => {
        if (!isObj(link)) return link;
        const { next, ...target } = link;
        return { target, action: { next: next === undefined ? null : next } };
      }),
    };
  }
  const every: Every = kind === "weighted_bw_biweekly" ? "week" : "session";
  return { rule: TABS_RULE, steps: [{ target: { every }, action: { rule: config } }] };
}

// ── The live tab ──────────────────────────────────────────────────────────────────────────────────────────────────────

/** `type` absent = regular (a week written before 4.1a′ carries none). */
export interface TabSetFacts {
  reps: number | null; kg: number | null; seconds: number | null; rest: number | null; type?: SetType;
}
/** `CoachTabs.Week` — `row` (4.1b): the row's tempo · note · each side that week; absent = not stated (never evidence that a
 *  row edit SHOWED). */
export interface TabWeek {
  present: boolean; hidden: boolean; logged: LadderLogged[] | null; sets: TabSetFacts[]; row?: RowFacts | null;
}
export interface TabLive { index: number; due: boolean; weeksOn: number }

/** `CoachTabs.met`. */
export function met(target: TabTarget, week: TabWeek): boolean {
  if (week.logged === null) return false;
  if (target.kind === "perSet") return reachedTable(target.table, week.sets.length, week.logged);
  if (target.kind !== "numbers") return false;
  return reached({ next: "", reps: target.reps, kg: target.kg, seconds: target.seconds, sessions: target.sessions },
                 week.sets.length, week.logged);
}

/** `CoachTabs.reachedTable` — `reached`, set by set: every prescribed working set completed, the k-th completed one at or
 *  above the table's k-th row (a set past the table reads its LAST row); a number the row names and the log lacks is not met. */
export function reachedTable(table: readonly TabTargetSet[], prescribedSets: number, logged: readonly LadderLogged[]): boolean {
  const done = logged.filter((s) => s.status !== "warmup" && (s.status === "completed" || s.status === "partial"));
  if (!(prescribedSets > 0 && done.length >= prescribedSets) || table.length === 0) return false;
  return done.every((set, k) => {
    const e = table[Math.min(k, table.length - 1)]!;
    if (e.reps !== null && (set.reps === null || !(set.reps >= e.reps))) return false;
    if (e.seconds !== null && (set.seconds === null || !(set.seconds >= e.seconds))) return false;
    if (e.kg !== null && (set.kg === null || !(set.kg + 1e-9 >= e.kg))) return false;
    return true;
  });
}

/** `CoachTabs.shows`. */
export function shows(action: TabAction, after: TabWeek): boolean {
  switch (action.kind) {
    case "set": {
      const c = action.change;
      if (!after.present || after.sets.length === 0) return false;
      // 4.1b — a row field the change names must be on the row the week after.
      if (touchesRow(c)) {
        const row = after.row ?? null;
        if (row === null) return false;
        if (c.tempo !== null && row.tempo !== c.tempo) return false;
        if (c.note !== null && row.note !== c.note) return false;
        if (c.eachSide !== null && row.eachSide !== c.eachSide) return false;
      }
      if (c.table !== null) {
        // The table is the whole prescription: the week after's WORKING sets are its working rows, field for field.
        const rows = c.table.filter((r) => r.type !== "warm_up");
        return after.sets.length === rows.length && after.sets.every((s, i) => {
          const r = rows[i]!;
          return s.reps === r.reps && s.seconds === r.seconds && s.rest === r.rest && (s.type ?? "regular") === r.type
            && Math.abs((s.kg ?? 0) - (r.kg ?? 0)) < 1e-9;
        });
      }
      if (c.sets !== null && after.sets.length !== c.sets) return false;
      return after.sets.every((s) => {
        if (c.reps !== null && s.reps !== c.reps) return false;
        if (c.kg !== null && !(Math.abs((s.kg ?? 0) - c.kg) < 1e-9)) return false;
        if (c.seconds !== null && s.seconds !== c.seconds) return false;
        if (c.rest !== null && s.rest !== c.rest) return false;
        return true;
      });
    }
    case "next": return !after.present;
    case "deactivate": return after.present && after.hidden;
    case "activate": return after.present && !after.hidden;
    case "rule": return false;
  }
}

/** `CoachTabs.liveStep` — `weeks` oldest first; the LAST is the current week. */
export function liveStep(steps: readonly TabStep[], weeks: readonly TabWeek[]): TabLive {
  let p = 0, streak = 0, on = 0;
  for (let i = 0; i < weeks.length; i++) {
    if (p >= steps.length) break;
    const step = steps[p]!;
    const week = weeks[i]!;
    let hit = false;
    if (step.target.kind === "every") return { index: p, due: false, weeksOn: 0 };
    if (step.target.kind === "weeks") {
      if (!week.present) continue;
      on += 1;
      hit = on >= step.target.weeks;
    } else {
      // The numbers, uniform or per set.
      if (!week.present || week.logged === null) continue;
      streak = met(step.target, week) ? streak + 1 : 0;
      hit = streak >= step.target.sessions;
    }
    if (!hit) continue;
    if (i === weeks.length - 1) return { index: p, due: true, weeksOn: on };
    if (shows(step.action, weeks[i + 1]!)) { p += 1; streak = 0; on = 0; }
  }
  if (p < steps.length && steps[p]!.target.kind === "weeks") return { index: p, due: false, weeksOn: on };
  return { index: p, due: false, weeksOn: 0 };
}

/** `CoachTabs.apply` — the working sets with the change written on them; ramp sets untouched; renumbered. A TABLE is
 *  the whole set table: next week's sets ARE its rows (ramp rows included, 0 kg a blank weight). */
export function applyChange(c: TabChange, sets: SetRow[]): SetRow[] {
  if (c.table !== null) {
    return c.table.map((r, i) => ({ position: i, reps: r.reps, weightKg: r.kg === null || r.kg === 0 ? null : r.kg,
                                    timeSeconds: r.seconds, restSeconds: r.rest, setType: r.type }));
  }
  const working = sets.filter((s) => !isRampSet(s));
  const count = c.sets ?? working.length;
  const rebuilt = (i: number): SetRow => {
    const base = i < working.length ? working[i]! : (working[working.length - 1]
      ?? { position: 0, reps: null, weightKg: null, timeSeconds: null, restSeconds: null, setType: "regular" });
    const s: SetRow = { ...base };
    if (c.reps !== null) s.reps = c.reps;
    if (c.seconds !== null) s.timeSeconds = c.seconds;
    if (c.kg !== null) s.weightKg = c.kg === 0 ? null : c.kg;
    if (c.rest !== null) s.restSeconds = c.rest;
    return s;
  };
  const out: SetRow[] = [];
  let w = 0;
  for (const set of sets) {
    if (isRampSet(set)) { out.push(set); continue; }
    if (w < count) out.push(rebuilt(w));
    w += 1;
  }
  while (w < count) { out.push(rebuilt(w)); w += 1; }
  return out.map((s, i) => ({ position: i, reps: s.reps, weightKg: s.weightKg, timeSeconds: s.timeSeconds,
                              restSeconds: s.restSeconds, setType: s.setType }));
}

// ── Words ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

/** `CoachTabs.targetWords`. A per-set target: runs of equal sets, "2 × 8 reps @ 20 kg · 6 reps @ 22.5 kg". */
export function targetWords(target: TabTarget): string {
  if (target.kind === "numbers") return targetText(target);
  if (target.kind === "perSet") {
    const text = (e: TabTargetSet) => targetText({ reps: e.reps, kg: e.kg, seconds: e.seconds, sessions: 1 });
    return runs(target.table, (a, b) => a.reps === b.reps && a.kg === b.kg && a.seconds === b.seconds)
      .map(([e, n]) => (n > 1 ? `${n} × ` : "") + text(e)).join(" · ") + (target.sessions === 2 ? " twice running" : "");
  }
  if (target.kind === "weeks") return target.weeks === 1 ? "1 week" : `${target.weeks} weeks`;
  return target.every === "session" ? "every clean session" : "every clean week";
}

/** Consecutive equal items as (item, count) — `CoachTabs.runs`. */
function runs<T>(list: readonly T[], same: (a: T, b: T) => boolean): [T, number][] {
  const out: [T, number][] = [];
  for (const x of list) {
    const last = out[out.length - 1];
    if (last !== undefined && same(last[0], x)) last[1] += 1;
    else out.push([x, 1]);
  }
  return out;
}

/** `CoachTabs.tableWords` — "1 warm-up set, 3 × 5 @ 60 kg, 1 × 3 @ 65 kg to failure rest 180 s": the warm-ups counted,
 *  the working rows as runs (0 kg = no load), the rest only when every working row names the same one. */
export function tableWords(table: readonly TabSet[]): string {
  const warm = table.filter((r) => r.type === "warm_up").length;
  const rows = table.filter((r) => r.type !== "warm_up");
  const kg = (r: TabSet) => (r.kg === null || r.kg === 0 ? null : r.kg);
  const groups = runs(rows, (a, b) => a.type === b.type && a.reps === b.reps && a.seconds === b.seconds && kg(a) === kg(b))
    .map(([r, n]) => {
      const amount = r.reps !== null ? `${n} × ${r.reps}` : r.seconds !== null ? `${n} × ${r.seconds} s`
        : n === 1 ? "1 set" : `${n} sets`;
      const load = kg(r);
      return amount + (load !== null ? ` @ ${kgText(load)} kg` : "")
        + (r.type === "failure" ? " to failure" : r.type === "drop_set" ? " drop" : "");
    });
  const head = warm === 0 ? [] : [warm === 1 ? "1 warm-up set" : `${warm} warm-up sets`];
  const rest = rows[0]!.rest;
  return [...head, ...groups].join(", ")
    + (rest !== null && rows.every((r) => r.rest === rest) ? ` rest ${rest} s` : "");
}

/** `CoachTabs.changeWords` — "4 × 6 @ 27.5 kg", "6 reps", "rest 90 s". */
export function changeWords(c: TabChange): string {
  const parts: string[] = [];
  if (c.table !== null) parts.push(tableWords(c.table));
  const amount = c.reps !== null ? String(c.reps) : c.seconds !== null ? `${c.seconds} s` : null;
  if (c.sets !== null && amount !== null) parts.push(`${c.sets} × ${amount}`);
  else if (c.sets !== null) parts.push(`${c.sets} sets`);
  else if (amount !== null) parts.push(c.reps !== null ? `${amount} reps` : amount);
  if (c.kg !== null) parts.push(parts.length === 0 ? `${kgText(c.kg)} kg` : `@ ${kgText(c.kg)} kg`);
  if (c.rest !== null) parts.push(`rest ${c.rest} s`);
  if (c.tempo !== null) parts.push(`tempo ${c.tempo}`);
  if (c.eachSide !== null) parts.push(c.eachSide ? "each side" : "not each side");
  if (c.note !== null) parts.push("a new note");
  return parts.join(" ");
}

/** `CoachTabs.targetClause` — what was met, in the sentence: "8 reps on every set", "2 weeks on this step", "2 × 8 reps ·
 *  6 reps reached". */
export function targetClause(target: TabTarget): string {
  if (target.kind === "weeks") return `${targetWords(target)} on this step`;
  if (target.kind === "perSet") return `${targetWords(target)} reached`;
  return `${targetWords(target)} on every set`;
}

export function changedSentence(name: string, step: TabStep, change: TabChange): string {
  return `${name}: ${targetClause(step.target)} — next week ${changeWords(change)}.`;
}

export function tabNotYetSentence(name: string, step: TabStep, weeksOn: number): string {
  if (step.target.kind === "weeks") return `${name}: week ${weeksOn} of ${step.target.weeks} before its next change.`;
  if (step.target.kind === "perSet") return `${name}: stays until its sets reach ${targetWords(step.target)}.`;
  return `${name}: stays until every set reaches ${targetWords(step.target)}.`;
}

/** `CoachTabs.pausedSentence` (4.1b) — a met target whose action hides the row. */
export const pausedSentence = (name: string, step: TabStep): string =>
  `${name}: ${targetClause(step.target)} — paused from next week.`;

/** `CoachTabs.resumedSentence` (4.1b) — a met target whose action shows the row again. */
export const resumedSentence = (name: string, step: TabStep): string =>
  `${name}: ${targetClause(step.target)} — back next week.`;

export const splitSentence = (name: string): string =>
  `${name}: its next step changes the set count, but this exercise is split across rows to keep a superset even — `
  + "stays as written.";
